/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Analytics › Realtime (contract section 5): active users in the last 5 and 30 minutes, events per minute over the last 30,
 * the top screens of the last 5 minutes and the service's write failures, from GET /v1/admin/usage/realtime (the lens
 * without its window), read again every 15 seconds while the section is on screen.
 */
import { useEffect } from 'react';
import { Activity, Radio } from 'lucide-react';
import { api } from '../../lib/api';
import { normalizeRealtime, realtimeQuery, usageQueryString } from '../../lib/usage';
import { Callout, EmptyState, Grid, Kpi, Panel, Sparkline, Stack } from '../../ui';
import { count } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { NamedBars, UsageError, type SectionProps } from './parts';

export const REALTIME_REFRESH_MS = 15_000;

/** `refreshMs`: how often the section reads again while on screen (15 s; a test shortens it). */
export function UsageRealtime({ lens, refreshMs = REALTIME_REFRESH_MS }: SectionProps & { refreshMs?: number }) {
  const query = realtimeQuery(lens);
  const key = `realtime${usageQueryString(query)}`;
  const { load, reload, reloading, reloadError } = useLoad(() => api.usageRealtime(query).then(normalizeRealtime), key);
  useEffect(() => {
    const timer = setInterval(reload, refreshMs);
    return () => clearInterval(timer);
  }, [reload, refreshMs]);
  const data = load.state === 'ready' ? load.data : null;
  const loading = load.state === 'loading';
  const lastMinute = data?.perMinute.at(-1) ?? null;

  if (load.state === 'failed') return <Panel title="Right now"><UsageError title="Realtime could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /></Panel>;

  return (
    <Stack>
      {data && data.writeFailures > 0 ? (
        <Callout tone="bad" role="alert" title={`${count(data.writeFailures)} batch${data.writeFailures === 1 ? '' : 'es'} could not be written`}>
          The service answered 503 to these batches and the clients are retrying them; the counts below are missing what they hold until a write succeeds.
        </Callout>
      ) : null}
      {reloadError ? <Callout tone="warn" role="status">The last refresh failed; the figures are from the refresh before it.</Callout> : null}
      <Panel title="Right now" description="Refreshes every 15 seconds. The lens applies: the audience and surface chosen above, and the hidden admin screens.">
        <Grid cols={4} label="Realtime">
          <Kpi icon={Radio} label="Active now" loading={loading} value={data ? count(data.activeUsers.m5) : '—'} sub="people with an event in the last 5 minutes" />
          <Kpi label="Active in 30 minutes" loading={loading} value={data ? count(data.activeUsers.m30) : '—'} sub="people with an event in the last 30 minutes" />
          <Kpi icon={Activity} label="Events last minute" loading={loading} value={lastMinute === null ? '—' : count(lastMinute)} sub="screen views and actions" />
          <Kpi label="Write failures" loading={loading} value={data ? count(data.writeFailures) : '—'} sub={data && data.writeFailures > 0 ? 'batches the service could not store' : 'none since the service started'} />
        </Grid>
      </Panel>
      <Grid cols={2}>
        <Panel title="Events per minute" description="The last 30 minutes, oldest first">
          {loading ? <EmptyState compact title="Loading" /> : data && data.perMinute.some((value) => value > 0)
            ? <Sparkline values={data.perMinute} label="Events per minute over the last 30 minutes" tone="accent" />
            : <EmptyState compact icon={Activity} title="Nothing in the last 30 minutes">Events appear here within a minute of someone using Gyro.</EmptyState>}
        </Panel>
        <Panel title="Top screens" description="Most viewed in the last 5 minutes">
          {loading ? <EmptyState compact title="Loading" /> : (
            <NamedBars rows={data?.topScreens ?? []} label="Views in the last 5 minutes" limit={8} empty={<EmptyState compact icon={Radio} title="No screens in the last 5 minutes">The screens people are on appear here as they open them.</EmptyState>} />
          )}
        </Panel>
      </Grid>
    </Stack>
  );
}
